import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { dayCount, fmtCHF } from "@/lib/format";
import { Field, Sel } from "@/components/Field";
import { FormDialog, useSubmit, num, clean } from "./FormDialog";

const EMPTY = { room_id: "", date_from: "", date_to: "", cleaning: false, requester_name: "", requester_email: "", requester_phone: "", event_type: "", participants: "", notes: "" };

export const priceSummary = (room, from, to, cleaning) => {
  if (!room || !from || !to || to < from) return null;
  const days = dayCount(from, to);
  const hasDay = room.price_per_day != null;
  const clean = cleaning && room.cleaning_enabled ? Number(room.cleaning_price || 0) : null;
  if (!hasDay && clean == null) return { days, text: `${days} ${days === 1 ? "giorno" : "giorni"}` };
  const parts = [hasDay && `${days} × ${fmtCHF(room.price_per_day)}`, clean != null && `pulizia ${fmtCHF(clean)}`].filter(Boolean);
  return { days, text: `${parts.join(" + ")} = ${fmtCHF((hasDay ? room.price_per_day * days : 0) + (clean || 0))}` };
};

export const BookingDialog = ({ open, onOpenChange, booking, rooms, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const { saving, run } = useSubmit();
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const room = (rooms || []).find((r) => r.id === f.room_id);

  useEffect(() => {
    if (!open) return;
    setF(booking ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, booking[k] ?? EMPTY[k]])), date_from: booking.start.slice(0, 10), date_to: booking.end.slice(0, 10), cleaning: !!booking.cleaning }
      : { ...EMPTY, room_id: rooms?.[0]?.id || "" });
  }, [open, booking, rooms]);

  const submit = () =>
    run(async () => {
      if (booking) await api.patch(`/bookings/${booking.id}`, clean({ date_from: f.date_from, date_to: f.date_to, cleaning: f.cleaning, notes: f.notes, participants: num(f.participants) }));
      else await api.post("/bookings", clean({ ...f, participants: num(f.participants) }));
      onOpenChange(false);
      onSaved?.();
    }, booking ? "Prenotazione aggiornata" : "Richiesta di prenotazione creata (in attesa)");

  const ro = !!booking;
  const sum = priceSummary(room, f.date_from, f.date_to, f.cleaning);
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={booking ? "Modifica prenotazione" : "Nuova prenotazione"} description="Le sale si prenotano a giornate intere. Le richieste restano in attesa finché non vengono confermate." onSubmit={submit} saving={saving} testId="booking-dialog">
      <Field label="Sala *"><Sel testId="booking-room-select" disabled={ro} value={f.room_id} onChange={(v) => setF((s) => ({ ...s, room_id: v, cleaning: false }))} options={(rooms || []).filter((r) => r.active || r.id === f.room_id).map((r) => ({ value: r.id, label: r.name }))} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Dal *"><Input data-testid="booking-date-from" type="date" required value={f.date_from} onChange={(e) => setF((s) => ({ ...s, date_from: e.target.value, date_to: !s.date_to || s.date_to < e.target.value ? e.target.value : s.date_to }))} className="h-11" /></Field>
        <Field label="Al *"><Input data-testid="booking-date-to" type="date" required min={f.date_from} value={f.date_to} onChange={set("date_to")} className="h-11" /></Field>
      </div>
      {room?.cleaning_enabled && (
        <label className="flex items-center gap-3 text-sm"><Checkbox data-testid="booking-cleaning" checked={f.cleaning} onCheckedChange={(c) => set("cleaning")(!!c)} /> Pulizia della sala ({fmtCHF(room.cleaning_price || 0)})</label>
      )}
      {sum && <div data-testid="booking-price-summary" className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">{sum.text}</div>}
      <Field label="Nome e cognome *"><Input data-testid="booking-name-input" required disabled={ro} value={f.requester_name} onChange={set("requester_name")} className="h-11" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="E-mail"><Input data-testid="booking-email-input" type="email" disabled={ro} value={f.requester_email} onChange={set("requester_email")} className="h-11" /></Field>
        <Field label="Telefono"><Input data-testid="booking-phone-input" disabled={ro} value={f.requester_phone} onChange={set("requester_phone")} className="h-11" /></Field>
        <Field label="Tipo di evento"><Input data-testid="booking-type-input" disabled={ro} value={f.event_type} onChange={set("event_type")} className="h-11" /></Field>
        <Field label="Partecipanti"><Input data-testid="booking-participants-input" type="number" min="1" value={f.participants} onChange={set("participants")} className="h-11" /></Field>
      </div>
      <Field label="Richieste aggiuntive"><Textarea data-testid="booking-notes-input" value={f.notes} onChange={set("notes")} rows={2} /></Field>
    </FormDialog>
  );
};
